import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { OrganizationPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function SponsorCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [fullName, setFullName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [industry, setIndustry] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [organizations, setOrganizations] = useState<OrganizationPublic[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getOrganizations().then((res) => {
      setOrganizations(res.organizations);
      if (res.organizations.length > 0) {
        setOrganizationId(res.organizations[0].id);
      }
    });
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.createSponsor({
        organization_id: organizationId,
        name,
        full_name: fullName || null,
        website_url: websiteUrl || null,
        industry: industry || null,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone || null,
      });
      navigate("/sponsors");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create sponsor");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="sponsor-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Sponsor</h1>
        <p className="text-muted-foreground">Create a new sponsor</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Sponsor Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="sponsor-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {organizations.length > 1 && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="organization">Organization</Label>
                <select
                  id="organization"
                  data-field="organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Sponsor Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. King Price"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="fullName">Full Company Name</Label>
              <Input
                id="fullName"
                data-field="full-name"
                placeholder="e.g. King Price Insurance Company Ltd"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <Input
                id="websiteUrl"
                data-field="website-url"
                type="url"
                placeholder="e.g. https://www.kingprice.co.za/"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                data-field="industry"
                placeholder="e.g. Short-term Insurance"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                data-field="contact-name"
                placeholder="e.g. John Doe"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                data-field="contact-email"
                type="email"
                placeholder="e.g. john@kingprice.co.za"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                data-field="contact-phone"
                placeholder="e.g. +27 11 123 4567"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting || !organizationId}>
                {submitting ? "Creating..." : "Create Sponsor"}
              </Button>
              <Link to="/sponsors" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
